import styled, { keyframes, css } from 'styled-components';

// ─── Sky-blue palette ─────────────────────────────────────────────────────────
// Primary:  #38bdf8 (sky-400)
// Light:    #7dd3fc (sky-300)
// Lighter:  #bae6fd (sky-200)
// Dark:     #0ea5e9 (sky-500)
// Darker:   #0284c7 (sky-600)
// BG base:  #071828 → #0c2640 → #0f3050

// ─── Animations ───────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export const Sidebar = styled.aside<{ expand: boolean }>`
  width: ${p => p.expand ? 'clamp(210px, 22vw, 255px)' : '62px'};
  min-width: ${p => p.expand ? 'clamp(210px, 22vw, 255px)' : '62px'};
  height: 100vh;
  background: linear-gradient(160deg, #071828 0%, #0c2640 45%, #0f3050 70%, #071828 100%);
  color: white;
  display: flex;
  flex-direction: column;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              min-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  overflow-x: hidden;
  overflow-y: hidden;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.5), 1px 0 0 rgba(56, 189, 248, 0.08);

  /* Subtle star-field texture */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(1px 1px at 20% 30%, rgba(56,189,248,0.25) 0%, transparent 100%),
      radial-gradient(1px 1px at 70% 60%, rgba(125,211,252,0.18) 0%, transparent 100%),
      radial-gradient(1px 1px at 45% 80%, rgba(14,165,233,0.15)  0%, transparent 100%);
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    width: ${p => p.expand ? '250px' : '0'};
    min-width: ${p => p.expand ? '250px' : '0'};
    transform: ${p => p.expand ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

// ─── Toggle Button ────────────────────────────────────────────────────────────

export const ToggleButton = styled.button`
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border: none;
  cursor: pointer;
  color: white;
  padding: 0.8rem 1.1rem;
  font-size: 1.3rem;
  z-index: 1001;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(14, 165, 233, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px) scale(1.06);
    box-shadow: 0 8px 24px rgba(14, 165, 233, 0.6);
    background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
    svg { transform: rotate(90deg); }
  }

  &:active { transform: translateY(0) scale(0.98); }

  svg { transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1); }

  @media (max-width: 1024px) {
    position: fixed;
    top: 12px;
    left: 12px;
    border-radius: 12px;
    padding: 0.7rem;
    width: 48px;
    height: 48px;
  }
`;

// ─── Logo / Brand ─────────────────────────────────────────────────────────────

export const Logo = styled.div<{ expand: boolean }>`
  margin: auto 0;
  font-size: clamp(0.9rem, 1.8vw, 1.05rem);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  display: ${p => p.expand ? 'block' : 'none'};
  padding: 0.4rem 0.2rem;
  white-space: nowrap;
  overflow: hidden;

  /* Animated sky-blue shimmer text */
  background: linear-gradient(90deg,
    #7dd3fc 0%, #bae6fd 25%, #38bdf8 50%, #7dd3fc 75%, #bae6fd 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 4s linear infinite, ${slideIn} 0.4s ease-out;
`;

// ─── Nav Wrapper ──────────────────────────────────────────────────────────────

export const Nav = styled.ul`
  flex: 1;
  padding: 0.5rem 0;
  list-style: none;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(100vh - 80px);
  margin: 0;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(56, 189, 248, 0.2);
    border-radius: 2px;
    transition: background 0.3s;
  }
  &:hover::-webkit-scrollbar-thumb { background: rgba(56, 189, 248, 0.5); }
  scrollbar-width: thin;
  scrollbar-color: rgba(56, 189, 248, 0.2) transparent;
`;

// Section label
export const NavSection = styled.div<{ expand: boolean }>`
  display: ${p => p.expand ? 'block' : 'none'};
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(125, 211, 252, 0.45);
  padding: 1rem 1.25rem 0.35rem;
  user-select: none;
`;

// ─── Nav Item ─────────────────────────────────────────────────────────────────

export const NavItem = styled.li`
  position: relative;
  animation: ${slideIn} 0.35s ease-out both;

  &:nth-child(1)  { animation-delay: 0.03s; }
  &:nth-child(2)  { animation-delay: 0.06s; }
  &:nth-child(3)  { animation-delay: 0.09s; }
  &:nth-child(4)  { animation-delay: 0.12s; }
  &:nth-child(5)  { animation-delay: 0.15s; }
  &:nth-child(6)  { animation-delay: 0.18s; }
  &:nth-child(7)  { animation-delay: 0.21s; }
  &:nth-child(8)  { animation-delay: 0.24s; }
  &:nth-child(9)  { animation-delay: 0.27s; }
  &:nth-child(10) { animation-delay: 0.30s; }
  &:nth-child(11) { animation-delay: 0.33s; }
  &:nth-child(12) { animation-delay: 0.36s; }
`;

// ─── NavLink ──────────────────────────────────────────────────────────────────

export const NavLink = styled.a.withConfig({
  shouldForwardProp: p => !['expand', 'isActive'].includes(p),
})<{ expand: boolean; isActive?: boolean }>`
  padding: 0.6rem 1.1rem;
  color: ${p => p.isActive ? '#bae6fd' : 'rgba(255,255,255,0.72)'};
  display: flex;
  align-items: center;
  white-space: nowrap;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: ${p => p.isActive ? '600' : '400'};
  position: relative;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0 10px 10px 0;
  margin: 1px 8px 1px 0;

  ${p => p.isActive && css`
    background: linear-gradient(90deg, rgba(56,189,248,0.2) 0%, rgba(56,189,248,0.05) 100%);
    box-shadow: inset 0 0 0 1px rgba(56,189,248,0.15);

    &::before {
      content: '';
      position: absolute;
      left: 0; top: 4px; bottom: 4px;
      width: 3px;
      border-radius: 0 3px 3px 0;
      background: linear-gradient(180deg, #38bdf8 0%, #7dd3fc 100%);
      box-shadow: 0 0 10px rgba(56, 189, 248, 0.7);
    }
  `}

  &:hover {
    color: #e0f2fe;
    background: linear-gradient(90deg, rgba(14,165,233,0.18) 0%, rgba(14,165,233,0.04) 100%);
    transform: translateX(4px);
    svg { color: #7dd3fc; filter: drop-shadow(0 0 6px rgba(56,189,248,0.6)); }
  }

  &:active { transform: translateX(2px); }

  svg {
    font-size: 1.15rem;
    flex-shrink: 0;
    color: ${p => p.isActive ? '#7dd3fc' : 'rgba(255,255,255,0.5)'};
    transition: all 0.25s ease;
    min-width: 1.15rem;
    margin-left: 0.15rem;
  }

  span {
    margin-left: 0.7rem;
    display: ${p => p.expand ? 'inline' : 'none'};
    transition: opacity 0.2s;
  }

  @media (max-width: 1024px) {
    padding: 0.55rem 1rem;
    font-size: 0.87rem;
  }
`;

// Chevron for dropdown
export const Chevron = styled.span<{ open: boolean }>`
  margin-left: auto;
  display: flex;
  align-items: center;
  font-size: 0.62rem;
  opacity: 0.55;
  transition: transform 0.3s ease;
  transform: ${p => p.open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

// ─── Dropdown ─────────────────────────────────────────────────────────────────

export const Dropdown = styled.ul<{ expand: boolean }>`
  list-style: none;
  padding: 0.25rem 0;
  margin: 0;
  background: linear-gradient(180deg,
    rgba(14,165,233,0.07) 0%,
    rgba(14,165,233,0.02) 100%);
  border-left: 1px solid rgba(56,189,248,0.18);
  margin-left: 1.6rem;
  border-radius: 0 0 8px 0;
  animation: ${fadeDown} 0.22s ease-out;
`;

export const DropdownItem = styled.li`
  padding: 0.55rem 1rem 0.55rem 1.1rem;
  color: rgba(255,255,255,0.62);
  font-size: 0.83rem;
  transition: all 0.2s ease;
  border-left: 2px solid transparent;
  border-radius: 0 8px 8px 0;
  cursor: pointer;

  &:hover {
    color: #bae6fd;
    background: linear-gradient(90deg, rgba(56,189,248,0.15) 0%, transparent 100%);
    border-left-color: #38bdf8;
    transform: translateX(3px);
  }
`;

// ─── Footer ───────────────────────────────────────────────────────────────────

export const Footer = styled.div`
  padding: 0.75rem 0.5rem;
  border-top: 1px solid rgba(56, 189, 248, 0.12);
  flex-shrink: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.25) 100%);
`;
